import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Building2, Menu } from "lucide-react";

export default function PublicNavbar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const dashboardPath = user?.role === "admin" || user?.role === "super_admin" ? "/admin" : "/tenant";
  const isHero = location.pathname === "/";
  const light = scrolled || !isHero;

  const navText = light ? "text-slate-600 hover:text-sky-600" : "text-white/80 hover:text-white";
  const logoMain = light ? "text-slate-900" : "text-white";
  const logoSub = light ? "text-sky-600" : "text-amber-300";
  const btnGhost = light ? "text-slate-700" : "text-white hover:bg-white/10";
  const menuIcon = light ? "text-slate-900" : "text-white";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${light ? "glass bg-white/90 shadow-sm" : "bg-transparent"}`}
      data-testid="public-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
            <div className="h-9 w-9 rounded-lg bg-sky-500 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className={`font-heading text-lg font-extrabold tracking-tight leading-none ${logoMain}`}>LEGACY</span>
              <span className={`font-heading text-[10px] font-bold tracking-[0.3em] block leading-none ${logoSub}`}>HEIGHTS</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {[
              { label: "Home", href: "/" },
              { label: "Rooms", href: "/rooms" },
              { label: "Community", href: "/community" },
            ].map((l) => (
              <Link key={l.href} to={l.href} className={`text-sm font-medium transition-colors duration-200 ${navText}`} data-testid={`nav-${l.label.toLowerCase()}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Button onClick={() => navigate(dashboardPath)} className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6 h-10" data-testid="nav-dashboard-btn">
                Dashboard
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/login")} className={`rounded-full px-5 h-10 ${btnGhost}`} data-testid="nav-login-btn">
                  Sign In
                </Button>
                <Button onClick={() => navigate("/register")} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-6 h-10 font-semibold" data-testid="nav-apply-btn">
                  Apply Now
                </Button>
              </>
            )}
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className={menuIcon} data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-slate-900 text-white border-none">
              <div className="flex flex-col gap-6 mt-8">
                <Link to="/" onClick={() => setOpen(false)} className="text-lg font-heading font-bold">Home</Link>
                <Link to="/rooms" onClick={() => setOpen(false)} className="text-lg font-heading font-bold">Rooms</Link>
                <Link to="/community" onClick={() => setOpen(false)} className="text-lg font-heading font-bold">Community</Link>
                <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
                  {user ? (
                    <Button onClick={() => { navigate(dashboardPath); setOpen(false); }} className="bg-sky-500 text-white rounded-full">Dashboard</Button>
                  ) : (
                    <>
                      <Button variant="outline" className="border-white/20 text-white rounded-full" onClick={() => { navigate("/login"); setOpen(false); }}>Sign In</Button>
                      <Button className="bg-amber-500 text-white rounded-full" onClick={() => { navigate("/register"); setOpen(false); }}>Apply Now</Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
